<template>
  <div class="surface-beethoven-preview">
    <div v-if="!isLoaded" class="progress-container">
      <div class="progress">
        <div class="progress-indeterminate" />
      </div>
    </div>
    <a v-if="isLoaded && showPreview"
       :href="url"
       target="_blank"
       rel="noopener nofollow"
       :class="{ 'beethoven-preview': true, 'is-fallback': isFallback }"
       :data-category="contentCategory"
       :data-subcategory="contentSubcategory"
       :download="downloadFileName"
       draggable="false"
       :data-luminance="previewImageLuminance"
       @click="play">
      <base-image draggable="false"
                  :alt="title"
                  :class="{ 'beethoven-preview-image': true, 'is-tiny': isTiny }"
                  :src="previewImageSrc"
                  :post-width="postWidth"
                  :post-height="postHeight"
                  :width="previewImageWidth"
                  :height="previewImageHeight"
                  :background-color="previewImageColor"
                  :lazy="true"
                  :data-aspect-ratio="previewImageAspectRatio" />
      <div v-if="showMeta" class="beethoven-preview-meta" :style="{ 'background-color': previewImageColor }">
        <div class="beethoven-title" :title="title">{{ title }}</div>
        <div v-if="description"
             class="beethoven-description"
             :title="description">{{ description }}</div>
        <div v-if="providerName" class="beethoven-provider">
          <div class="beethoven-provider-name caption-type">{{ providerName }}</div>
        </div>
      </div>
      <i v-if="contentCategory == 'video'" class="beethoven-video-play-action beethoven-action" />
      <i v-if="contentCategory == 'audio'" class="beethoven-audio-play-action beethoven-action" />
      <i v-if="contentCategory == 'file'" class="beethoven-file-extension">{{ extension }}</i>
    </a>
    <div v-if="showEmbed" class="beethoven-inline-embed">
      <div class="beethoven-inline-embed-container"
           :style="{ 'background-color': previewImageColor }"
           :data-aspect-ratio="previewImageAspectRatio"
           v-html="embedHtml" />
      <div class="beethoven-inline-embed-actions"
           :style="{'background-color': previewImageColor, height: embedMetaHeight + 'px'}"
           :data-luminance="previewImageLuminance">
        <div class="button-list">
          <button class="icon-button" @click="backToPreview" v-t:title="'Back to preview'">
            <base-icon name="arrow_back" />
          </button>
        </div>
        <div class="button-list">
          <button class="icon-button" @click="zoom" v-t:title="'Zoom'">
            <base-icon name="zoom_in" />
          </button>
          <a v-if="isDownloadable"
             target="_blank"
             :download="title"
             :href="url"
             class="icon-button"
             :title="__('Download')"
             draggable="false">
            <base-icon name="download" />
          </a>
          <button class="icon-button"
                  @click="openInNew"
                  v-t:title="'Open in new window'">
            <base-icon name="open_in_new" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { $ } from 'bits/jquery'
import IntlMixin from 'vuemixins/intl_mixin'
import BaseImage from 'vuecomponents/base_image'
import BaseIcon from 'vuecomponents/base_icon'
import { hyphenatedCurrentLocale as locale } from 'bits/current_locale'
import { getDisplayAttributes } from '@padlet/beethoven-client'

export default {
  components: { BaseIcon, BaseImage },
  mixins: [IntlMixin],
  props: ['post', 'url', 'width', 'height', 'actionable'],
  data () {
    return {
      canBeActioned: this.actionable !== 'no',
      showPreview: true,
      showEmbed: false,
      link: null,
      isLoaded: null,
      isTiny: null,
      isFallback: null,
      previewImageSrc: null,
      previewImageWidth: null,
      previewImageHeight: null,
      previewImageColor: null,
      previewImageAspectRatio: null,
      previewImageLuminance: null,
      showMeta: null,
      title: null,
      downloadFileName: null,
      description: null,
      providerName: null,
      contentCategory: null,
      contentSubcategory: null,
      extension: null,
      embedCode: null,
      isDownloadable: null,
      showHtml: false,
      embedHtml: null,
      embedMetaHeight: null,
    }
  },
  computed: {
    ...mapState(['dpr', 'isApp', 'isMobile', 'isPhone', 'canPostMessage']),
    ...mapState('postAction', [
      'currentlyPlayingPostCid',
    ]),
    postWidth () {
      return this.width
    },
    postHeight () {
      return Math.floor(this.width / this.previewImageAspectRatio)
    },
  },
  watch: {
    width (newValue) {
      if (newValue > this.previewImageWidth) {
        this.$nextTick(() => {
          this.getBeethovenAttributes()
        })
      }
    },
    currentlyPlayingPostCid (newCid) {
      if (!this.showPreview && newCid && this.post.cid !== newCid) this.backToPreview()
    },
  },
  mounted () {
    this.$nextTick(() => {
      this.getBeethovenAttributes()
    })
  },
  beforeDestroy () {
    this.unsetAsCurrentlyPlayingPost()
  },
  methods: {
    ...mapActions('postAction', [
      'expandPost',
      'zoomPost',
      'setCurrentlyPlayingPost',
      'unsetCurrentlyPlayingPost',
    ]),
    ...mapActions('nativeApp', ['openLink']),
    addQueryParams (url, paramsString) {
      if (url.match(/\?/)) {
        return [url, paramsString].join('&')
      }
      return [url, paramsString].join('?')
    },
    playInApp () {
      let linkType = 'external'
      if (this.contentSubcategory === 'padlet') {
        linkType = 'padlet'
      } else if (this.contentCategory === 'photo' || this.contentCategory === 'file') {
        linkType = 'internal_mediaplayer'
      } else if (this.contentCategory === 'page' || this.contentCategory === 'document') {
        linkType = 'internal_webview'
      }

      this.openLink({
        url: this.url,
        linkType,
        contentCategory: this.contentCategory,
        contentSubcategory: this.contentSubcategory,
      })
    },
    unsetAsCurrentlyPlayingPost () {
      if (this.currentlyPlayingPostCid === this.post.cid) {
        this.unsetCurrentlyPlayingPost({ cid: this.post.cid })
      }
    },
    play (e) {
      if (!this.canBeActioned) {
        // opening link: cant be actioned
        return false
      }
      if ($(this.$el).parents('.noclick,.is-dragging').length > 0) {
        // opening link: dragging
        e.preventDefault()
        return false
      } if (this.contentCategory === 'video' || this.contentCategory === 'audio') {
        // opening link: content is video and audio so we display player
        this.embedInline()
        e.preventDefault()
        return false
      } if (this.isApp && this.canPostMessage) {
        // opening link: isApp so use postMessage to open link
        this.playInApp()
        return false
      } if (this.isPhone) {
        // opening link: on phone, only images zoom
        if (this.contentCategory === 'photo') {
          this.zoom()
          e.preventDefault()
          return false
        }
      } else if (this.embedCode && this.contentSubcategory !== 'padlet') {
        // opening link: display preview for file type having the embed code
        this.zoom()
        e.preventDefault()
        return false
      }
      // opening link: download or go to url
      return true
    },
    embedInline () {
      this.setCurrentlyPlayingPost({ cid: this.post.cid })
      this.showPreview = false
      this.showEmbed = true
      this.embedHtml = this.embedCode
      const $embed = $(this.embedCode)

      if ($embed.is('iframe')) {
        $embed.attr({
          width: this.postWidth || this.previewImageWidth,
          height: this.postHeight || this.previewImageHeight,
        }).css('background', this.previewImageColor)
        const currentSrc = $embed.attr('src')
        let newSrc = this.addQueryParams(currentSrc, 'autoplay=1')
        if (this.providerName.toLowerCase() === 'youtube') {
          newSrc = this.addQueryParams(newSrc, 'rel=0')
        }
        $embed.attr('src', newSrc)
        this.embedHtml = $embed[0].outerHTML
        this.embedMetaHeight = $('.beethoven-preview-meta', this.$el).height()
      }
      $(this.$el).parents('.wish').addClass('is-playing')
    },
    zoom () {
      this.backToPreview()
      this.zoomPost({
        zoomedPostCid: this.post ? this.post.cid : null,
        zoomedLink: this.link,
      })
      if (this.post && this.post.id) {
        $(this.$el).parents('.wish').addClass('is-zoomed')
      }
    },
    backToPreview () {
      this.showPreview = true
      this.showEmbed = false
      this.unsetAsCurrentlyPlayingPost()
      return $(this.$el).parents('.wish').removeClass('is-playing is-zoomed')
    },

    expandPost () {
      this.backToPreview()
      this.expandPost({ postCid: this.post.cid })
    },
    openInNew () {
      if (this.isApp) {
        this.openLink({
          linkType: 'external',
          url: this.url,
          contentCategory: this.contentCategory,
          contentSubcategory: this.contentSubcategory,
        })
      } else {
        window.open(this.url)
      }
    },
    async getBeethovenAttributes () {
      const bhOptions = {
        dpr: this.dpr,
        locale: locale,
        width: this.width,
        height: this.height,
        updated_at: this.updatedAt,
      }
      const data = await getDisplayAttributes(this.url, bhOptions)
      this.link = data
      this.isLoaded = true
      this.isTiny = data.is_tiny
      this.isFallback = data.is_fallback
      this.previewImageSrc = data.image_url
      this.previewImageWidth = data.image_width
      this.previewImageHeight = data.image_height
      this.previewImageColor = data.image_color
      this.previewImageAspectRatio = data.image_aspect_ratio
      this.previewImageLuminance = data.image_luminance
      this.showMeta = data.show_meta
      this.title = data.title
      this.description = data.description
      this.providerName = data.provider_name
      this.contentCategory = data.content_category
      this.contentSubcategory = data.content_subcategory
      this.extension = data.extension
      this.embedCode = data.embed_code
      this.isDownloadable = data.is_downloadable && !this.isMobile
      if (this.contentCategory === 'file') {
        this.downloadFileName = this.title
      } else {
        this.downloadFileName = false
      }
      if (this.contentSubcategory === 'padlet' && (this.updatedAt == null)) {
        this.updatedAt = data.updated_at
        this.getBeethovenAttributes()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$photo-color: #FF4081;
$audio-color: #7C77B9;
$video-color: #FEC006;
$document-color: #7FC136;
$page-color: #4A90E2;
$file-color: #7B8693;
$title-color: #283238;
$description-color: #47525D;
$provider-color: #828990;

@import "./../../assets/stylesheets/3/modules/all";

.surface-beethoven-preview {
  display: block;

  .progress-container {
    padding: 16px;
  }
}

.beethoven-preview {
  font-family: $font-family;
  position: relative;
  display: block;
  color: var(--body-text-color);
  touch-action: manipulation;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  user-select: none;

  &:hover, &:visited, &:active, &:hover:visited {
    color: var(--body-text-color);
  }
}

.beethoven-preview-image {
  -webkit-touch-callout: none !important;
  background-size: 200px 200px;
  background-color: white;
  display: block;
  margin: 0px auto;
  max-width: 100%;
}

.beethoven-preview-image.beethoven-success:not(.is-tiny) {
  background-color: white !important;
}

.beethoven-fallback-icon {
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 50%;
  height: 72px;
  left: calc(50% - 36px);
  position: absolute;
  top: calc(50% - 36px);
  width: 72px;
}

// --------------------------------------------------------
// METADATA SECTION
// --------------------------------------------------------
.beethoven-preview-meta {
  padding: 8px 12px;
  min-height: 40px;
  text-align: left;
  background: $superlight-grey;
}

.beethoven-title {
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.1px;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.beethoven-description {
  font-size: 12px;
  font-weight: normal;
  letter-spacing: 0.1px;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.beethoven-provider-name {
  font-size: 11px;
  font-weight: normal;
  line-height: 16px;
  text-transform: lowercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.beethoven-preview:hover {
  .beethoven-preview-curtain {
    opacity: 1;
  }

  .beethoven-preview-meta {
    opacity: 1;
  }
}

.beethoven-video-symbol-small {
  background-color: #FFD300;
  background-image: url("./../../assets/images/video_symbol.svg");
  background-position: center center;
  background-repeat: no-repeat;
  border-radius: 50%;
  display: none;
  flex-shrink: 0;
  height: 8px;
  padding: 8px 7px;
  width: 10px;
}

.beethoven-audio-symbol-small {
  background-color: $audio-color;
  background-image: url("./../../assets/images/audio_symbol.svg");
  background-position: center center;
  background-repeat: no-repeat;
  border-radius: 50%;
  display: none;
  flex-shrink: 0;
  height: 11px;
  padding: 6px 8px 7px 8px;
  width: 8px;
}

.beethoven-document-symbol-small {
  background-color: #7FC136;
  background-image: url("./../../assets/images/document_symbol.svg");
  background-position: center center;
  background-repeat: no-repeat;
  border-radius: 50%;
  display: none;
  flex-shrink: 0;
  height: 13px;
  padding: 5px 6px 6px 8px;
  width: 10px;
}

.beethoven-generic-attachment {

}

// --------------------------------------------------------
// PHOTO
// --------------------------------------------------------
[data-category="photo"] {
  .beethoven-fail, &.is-fallback .beethoven-preview-image {
    background-image: url("./../../assets/images/3/fallback_image.png");
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }
}

// --------------------------------------------------------
// VIDEO
// --------------------------------------------------------
[data-category="video"] {
  .beethoven-fail, &.is-fallback .beethoven-preview-image {
    background-image: url("./../../assets/images/3/fallback_video.png");
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }

  .beethoven-video-play-action {
    background-image: url("./../../assets/images/play_action.png");
    background-repeat: no-repeat;
    background-size: contain;
    display: block;
    height: 28px;
    left: 10px;
    position: absolute;
    top: 10px;
    width: 28px;
  }
}

// --------------------------------------------------------
// AUDIO
// --------------------------------------------------------
[data-category="audio"] {
  .beethoven-fail, &.is-fallback .beethoven-preview-image {
    background-image: url("./../../assets/images/3/fallback_audio.png");
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }

  .beethoven-audio-play-action {
    background-image: url("./../../assets/images/play_action.png");
    background-repeat: no-repeat;
    background-size: contain;
    display: block;
    height: 28px;
    left: 10px;
    position: absolute;
    top: 10px;
    width: 28px;
  }
}

// --------------------------------------------------------
// DOCUMENT
// --------------------------------------------------------
[data-category="document"] {
  .beethoven-fail, &.is-fallback .beethoven-preview-image {
    background-image: url("./../../assets/images/3/fallback_document.png");
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }
}

[data-subcategory="spreadsheet"] {
  .beethoven-fail, &.is-fallback .beethoven-preview-image {
    background-image: url("./../../assets/images/3/fallback_spreadsheet.png");
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }
}

[data-subcategory="presentation"] {
  .beethoven-fail, &.is-fallback .beethoven-preview-image {
    background-image: url("./../../assets/images/3/fallback_presentation.png");
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }
}

// --------------------------------------------------------
// PAGE
// --------------------------------------------------------
[data-category="page"] {
  .beethoven-fail, &.is-fallback .beethoven-preview-image {
    background-image: url("./../../assets/images/3/fallback_link.png");
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }
}

// --------------------------------------------------------
// FALLBACK
// --------------------------------------------------------
[data-category="unknown"], [data-category="file"] {
  .beethoven-preview-image {
    background-image: url("./../../assets/images/3/fallback_file.png");
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }

  .beethoven-file-extension {
    display: block;
    position: absolute;
    color: $ultralight-grey;
    text-transform: uppercase;
    right: 16px;
    top: 8px;
    width: calc(100% - 20px);
    overflow: hidden;
    font-size: 24px;
    letter-spacing: 2px;
    font-weight: 700;
    text-align: right;
    text-shadow: -1px -1px 0 #D3D3D3,
      1px -1px 0 #D3D3D3,
      -1px 1px 0 #D3D3D3,
      1px 1px 0 #D3D3D3;
  }
}

// --------------------------------------------------------
// INLINE EMBED
// --------------------------------------------------------
.beethoven-inline-embed {
  position: relative;
  overflow: hidden;

  iframe {
    display: block;
  }
}
.beethoven-inline-embed-actions {
  padding: 8px;
  display: flex;
  align-items: flex-end;
  min-height: 40px;
  justify-content: space-between;
}
.beethoven-inline-embed-container {
  position: relative;
  overflow: hidden;
}

</style>
